/**
 * useMailboxConnections — the delegated-mailbox read hook + connect/disconnect
 * mutations (Story MC6). live/mock split is the `useAdminRead`/`useCarrierRows`
 * shape; the whole feature is feature-detected off `GET /mailbox/connections`.
 *
 * Three-valued availability (AC8), rendered from the server's answer — NEVER a
 * client-side re-derivation of auth rules (coding-standards rule #2):
 *   - query error / 404          → 'absent'   (module not landed → UI-U9/V10-J2 verbatim)
 *   - connect_enabled === false  → 'disabled' ("in Kürze verfügbar", no zero-state)
 *   - else                       → 'available' (within it, count > 0 gates real inbox/send)
 *
 * [Source: apps/web/src/features/administration/hooks.ts (useAdminRead)]
 * [Source: apps/web/src/features/communication-v2/hooks/useCommAssist.ts (feature-detect)]
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';
import { apiClient, ApiError } from '../../../lib/api-client';
import { MOCK_MAILBOX_CONNECTIONS, mockSyncNow } from '../mock';
import type {
  MailboxConnectionDto,
  MailboxConnectionsResponse,
  MailboxProvider,
  ManualSyncResult,
} from '../types';

/** available (enabled) · disabled (creds not seeded) · absent (module not landed). */
export type MailboxAvailability = 'available' | 'disabled' | 'absent';

export interface MailboxConnectionsState {
  availability: MailboxAvailability;
  connections: MailboxConnectionDto[];
  providers: MailboxProvider[];
  /** ≥1 connection in ANY status (a `revoked`/`error` row still counts as "not zero"). */
  hasConnection: boolean;
  /** The first `status:'connected'` connection = the send-from mailbox (v1: one primary). */
  primaryConnection: MailboxConnectionDto | null;
  isLoading: boolean;
}

/**
 * Read `GET /mailbox/connections` and derive the three-valued availability.
 * Mock mode always resolves the fixture (never errors); live mode degrades an
 * error/404 to `'absent'` so nothing breaks before the api lands.
 */
export function useMailboxConnections(): MailboxConnectionsState {
  const live = isLiveData();
  const query = useQuery<MailboxConnectionsResponse>({
    queryKey: qk.mailboxConnections(),
    queryFn: () => (live ? apiClient.mailbox.connections() : Promise.resolve(MOCK_MAILBOX_CONNECTIONS)),
    // Feature-detect: a missing module must NOT retry-storm; one shot, then 'absent'.
    retry: false,
    ...(live ? {} : { initialData: MOCK_MAILBOX_CONNECTIONS, staleTime: Infinity }),
  });

  const data = query.data;
  // While the live query is in flight (no data, no error) we render the pre-existing
  // screen (treat as 'absent') so the zero-state never flashes before we know.
  let availability: MailboxAvailability;
  if (query.isError || data == null) availability = 'absent';
  else if (!data.connect_enabled) availability = 'disabled';
  else availability = 'available';

  const connections = data?.connections ?? [];
  const primaryConnection = connections.find((c) => c.status === 'connected') ?? null;

  return {
    availability,
    connections,
    providers: data?.providers ?? [],
    hasConnection: connections.length > 0,
    primaryConnection,
    isLoading: query.isLoading,
  };
}

/**
 * Defense-in-depth: only ever hand an absolute **https** URL to the navigation sink.
 * The api owns state/PKCE/redirect_uri and the authorize_url is a first-party value,
 * but this guards the one web-side sink so a non-https / `javascript:` / off-scheme
 * value can never reach `window.location.assign` (open-redirect / script-URL hardening).
 * OAuth authorize endpoints (login.microsoftonline.com / accounts.google.com) are
 * always https, so this never rejects a legitimate URL.
 */
function assertHttpsAuthorizeUrl(raw: string): string {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    throw new Error('invalid_authorize_url');
  }
  if (url.protocol !== 'https:') throw new Error('invalid_authorize_url');
  return raw;
}

/**
 * Start the OAuth connect flow for one provider. On `{ authorize_url }` the browser
 * does a full-page hop to consent (the api owns state/PKCE/redirect_uri). Per-provider
 * pending is derived from `variables` so a single instance serves both cards. A
 * non-https authorize_url is rejected in the mutationFn → the mutation errors + the
 * card un-pends (never a silent no-op), and nothing is ever handed to the nav sink.
 */
export function useConnectMailbox() {
  const mutation = useMutation({
    mutationFn: async (provider: MailboxProvider) => {
      const res = await apiClient.mailbox.connect(provider);
      assertHttpsAuthorizeUrl(res.authorize_url);
      return res;
    },
    onSuccess: (res) => {
      window.location.assign(res.authorize_url);
    },
  });
  const pendingProvider: MailboxProvider | null =
    mutation.isPending && mutation.variables != null ? mutation.variables : null;
  return { ...mutation, pendingProvider };
}

/** Disconnect (revoke + remove) one connection → invalidate the list. Toast is the caller's. */
export function useDisconnectMailbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.mailbox.disconnect(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.mailboxConnections() });
    },
  });
}

/** A toast to show — kind drives the success/error styling; message is already user-safe. */
export interface MailboxSyncToast {
  kind: 'success' | 'error';
  message: string;
}

/**
 * Map a ManualSyncResult → the toast text (P2 hardening 06). Never shows the raw `error`
 * field — an errored mailbox always maps to the generic "neu verbinden" prompt.
 */
export function syncResultToToast(r: ManualSyncResult): MailboxSyncToast {
  if (r.status === 'error') {
    return { kind: 'error', message: 'Synchronisierung fehlgeschlagen: Postfach neu verbinden' };
  }
  if (r.ingested > 0) {
    const base = `Synchronisiert: ${r.ingested} neue E-Mails`;
    return { kind: 'success', message: r.has_more ? `${base} - weitere folgen automatisch` : base };
  }
  return { kind: 'success', message: 'Keine neuen E-Mails' };
}

/** Map a sync mutation error → the toast text (429 cooldown / 409 in-progress / generic). */
export function syncErrorToToast(err: unknown): MailboxSyncToast {
  if (err instanceof ApiError) {
    if (err.status === 429) {
      const s = err.retryAfterSeconds;
      return { kind: 'error', message: s ? `Bitte kurz warten (${s}s)` : 'Bitte kurz warten' };
    }
    if (err.status === 409) {
      return { kind: 'error', message: 'Synchronisierung läuft bereits' };
    }
  }
  return { kind: 'error', message: 'Synchronisierung fehlgeschlagen' };
}

/**
 * Manual "Jetzt synchronisieren" (P2 hardening 06): run one bounded round, then make the new
 * mail visible in one click — invalidate the connection list AND the Posteingang/Eingang query
 * families (the Eingang list is keyed under `['tasks','list']`; the overview buckets under
 * `['communications']`; counts/activity under `['posteingang']`). Toast is the caller's
 * (use `syncResultToToast` / `syncErrorToToast`).
 */
export function useSyncMailboxNow() {
  const qc = useQueryClient();
  const live = isLiveData();
  return useMutation<ManualSyncResult, Error, string>({
    mutationFn: (id: string) => (live ? apiClient.mailbox.syncNow(id) : mockSyncNow(id)),
    onSuccess: () => {
      // The new mail is on screen without a reload: refresh every list the Posteingang renders.
      void qc.invalidateQueries({ queryKey: ['posteingang'] });
      void qc.invalidateQueries({ queryKey: ['tasks', 'list'] });
      void qc.invalidateQueries({ queryKey: ['communications'] });
    },
    onSettled: () => {
      // Always refresh the connection list so "Zuletzt synchronisiert" moves on demand.
      void qc.invalidateQueries({ queryKey: qk.mailboxConnections() });
    },
  });
}
